import { useCallback, useEffect, useMemo } from 'react'

import { usePrintScale } from '@editor/hooks/usePrintScale'
import { DoubleSide, Matrix4, Mesh, PlaneGeometry, ShaderMaterial } from 'three'

import {
  GRID_FRAGMENT_SHADER,
  GRID_GRID_COLOR_RGB,
  GRID_LINE_WIDTH,
  GRID_OPACITY,
  GRID_VERTEX_SHADER,
} from '../constants'

const useCanvasGrid = (size: number, gridOpacity: number = GRID_OPACITY) => {
  // Print scale hook
  const { currentPrintScale } = usePrintScale()

  // Grid material with shader for grid pattern
  const material = useMemo(() => {
    const gridSize = 0.01 / currentPrintScale // 1cm gap in world units
    const canvasSize = size // Capture size for shader uniform

    return new ShaderMaterial({
      uniforms: {
        gridSize: { value: gridSize },
        canvasSize: { value: canvasSize },
        gridColor: { value: GRID_GRID_COLOR_RGB },
        lineWidth: { value: GRID_LINE_WIDTH },
        gridOpacity: { value: gridOpacity },
      },
      vertexShader: GRID_VERTEX_SHADER,
      fragmentShader: GRID_FRAGMENT_SHADER,
      transparent: true,
      side: DoubleSide,
      depthWrite: false,
      depthTest: false,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPrintScale, size])

  useEffect(() => {
    if (material instanceof ShaderMaterial && 'gridOpacity' in material.uniforms) {
      material.uniforms.gridOpacity.value = gridOpacity
    }
  }, [gridOpacity, material])

  const makeGridMesh = useCallback(
    (name: string, matrix: Matrix4) => {
      const geometry = new PlaneGeometry(size, size)
      const mesh = new Mesh(geometry, material)
      mesh.name = name

      mesh.applyMatrix4(matrix)
      mesh.renderOrder = -2

      return mesh
    },
    [material, size],
  )

  return { makeGridMesh }
}

export default useCanvasGrid
